/**
 * The local cache for a SAFE workshop document, and the export/import file handling around it.
 *
 * **The server is the record now.** A SAFE assessment belongs to a workshop
 * (`api/db/workshop/<id>/state/`), which is what makes it visible to a colleague, resumable from
 * another machine, and attributable to a Salesforce Account. The session API is still stateless —
 * see `safeApi.ts` — so the client owns the document and writes it back after every round trip;
 * this module is no longer *the* memory, only a cushion under it.
 *
 * What the cushion is for: the write to the server can fail (a lapsed session, a dropped network)
 * while the assessment carries on perfectly well in the page. Without a local copy, closing that tab
 * loses the work between the last successful save and the failure. So every commit is also written
 * here, and on load the cache is used when it is *newer* than what the server holds — see
 * `useSafeSession`, which then pushes it back up so the two agree again.
 *
 * One slot per workshop id, not per mode. Two SEs' workshops, or two workshops for the same account,
 * are different documents; keying by mode (as v1 did) meant the second overwrote the first.
 *
 * Deliberately `localStorage` rather than `sessionStorage`: closing a tab mid-workshop after a failed
 * save is exactly the case this exists for.
 *
 * Note on v1: `stb.safe.workshop.v1.risk` / `…architect` entries from before assessments were bound
 * to a workshop are left where they are rather than cleaned up. They cannot be attributed to a
 * workshop — nobody knows which customer they were for — and silently deleting somebody's only copy
 * of an in-progress assessment is worse than leaving two unread keys behind.
 */
import { SafeAssessmentDocument, SafeWorkshop } from './safeApi';

/** Bumped when the slot's meaning changes. v2 is keyed by workshop id; v1 was keyed by mode. */
const STORAGE_VERSION = 2;

const storageKey = (workshopId: number | string) =>
  `stb.safe.workshop.v${STORAGE_VERSION}.${workshopId}`;

type StoredWorkshop = {
  version: number;
  workshopId: number | string;
  savedAt: string;
  workshop: SafeWorkshop;
};

export type CachedWorkshop = {
  workshop: SafeWorkshop;
  /** When this copy was written, for comparing against the server's `stateUpdated`. */
  savedAt: Date;
};

/**
 * Read the cached document for one workshop, or null.
 *
 * Every failure path returns null rather than throwing: a corrupted or hand-edited entry should cost
 * the cushion, not the ability to open the workshop at all. A bad entry is cleared on the way out so
 * the same value is not re-parsed on every mount.
 */
export function loadWorkshopCache(workshopId: number | string): CachedWorkshop | null {
  try {
    const raw = window.localStorage.getItem(storageKey(workshopId));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredWorkshop;

    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== STORAGE_VERSION) return null;
    if (!parsed.workshop || typeof parsed.workshop !== 'object') return null;

    const savedAt = new Date(parsed.savedAt);

    // An entry with no readable timestamp cannot be compared against the server, and "use it
    // anyway" would risk replaying an old document over newer server state.
    if (Number.isNaN(savedAt.getTime())) return null;

    return { workshop: parsed.workshop, savedAt };
  } catch {
    try {
      window.localStorage.removeItem(storageKey(workshopId));
    } catch {
      /* storage is unavailable entirely; nothing to clean up */
    }

    return null;
  }
}

/**
 * Cache the document returned by the last session call.
 *
 * Silent on failure. Storage can be full or blocked (private browsing, a strict policy) and the
 * assessment is still being saved to the server regardless — interrupting a workshop with a storage
 * error would be the worse outcome.
 */
export function saveWorkshopCache(
  workshopId: number | string,
  workshop: SafeWorkshop | null | undefined,
): void {
  if (!workshop) return;

  const record: StoredWorkshop = {
    version: STORAGE_VERSION,
    workshopId,
    savedAt: new Date().toISOString(),
    workshop,
  };

  try {
    window.localStorage.setItem(storageKey(workshopId), JSON.stringify(record));
  } catch {
    /* full, blocked, or unavailable — see above */
  }
}

/** Forget the cached copy. Used by "Start over", which also clears the server's bag. */
export function clearWorkshopCache(workshopId: number | string): void {
  try {
    window.localStorage.removeItem(storageKey(workshopId));
  } catch {
    /* nothing to do */
  }
}

/* -------------------------------------------------------------------- transfer */

/**
 * Download an assessment document as a JSON file.
 *
 * The filename comes from the server (`export.filename`, e.g.
 * `SAFE-export-2026-08-31T09:41:07.json`) so a file exported here is named identically to one
 * exported by the original framework tool.
 */
export function downloadAssessment(
  assessment: SafeAssessmentDocument,
  filename: string,
): void {
  const blob = new Blob([JSON.stringify(assessment, null, 2)], {
    type: 'application/json',
  });

  const url = window.URL.createObjectURL(blob);

  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename || 'SAFE-export.json';
    anchor.rel = 'noopener';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    // Revoked on the way out either way: the object URL pins the blob in memory otherwise, and
    // a workshop can export many times in a session.
    window.URL.revokeObjectURL(url);
  }
}

/**
 * Read a picked file as JSON.
 *
 * Only shape-checks that it parsed to an object. Whether it is a *SAFE* export is the server's
 * call (`POST db/safe/assessment/validate/`), which knows the required keys and reports them —
 * duplicating that check here would give two answers to one question.
 */
export async function readAssessmentFile(file: File): Promise<any> {
  const text = await file.text();

  let parsed: any;

  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(`${file.name} is not valid JSON.`);
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`${file.name} does not contain a SAFE assessment.`);
  }

  return parsed;
}
